import React from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import clsx from 'clsx';
import { Colors, Metrics, Images } from '../../themes';
import { withRouter } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { navItems as routesData } from '../../Routes';
import LanguageSelector from './LanguageSelector';
import SvgIcon from '../../icons/SvgIcon';
import { checkRole } from '../../lib/Utils';
import Button from '@material-ui/core/Button';

const useStyles = makeStyles((theme) => {
  return {
    button: {
      margin: theme.spacing(2),
      fontSize: 14,
      color: Colors.white,
      border: `1px solid ${Colors.disabledGrey}`,
      '&:hover': {
        background: Colors.disabledGrey,
      },
      '&:disabled': {
        cursor: 'not-allowed',
        pointerEvents: 'all !important',
        border: `1px solid ${Colors.disabledGrey}`,
        background: Colors.navBackgroundGrey,
        '&:hover': {
          background: Colors.navBackgroundGrey,
        },
      },
    },
    saveButton: {
      color: Colors.white,
      background: Colors.blue,
      border: 'none',
      '&:hover': {
        background: Colors.lightBlue,
      },
    },
    smallSizeButton: {
      height: 35,
      fontSize: 12,
      paddingLeft: 12,
      paddingRight: 12,
    },
  };
});

const TopContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${({ isControllers }) =>
    isControllers
      ? css`
          @media (max-width: ${Metrics.breakpoints.lg + Metrics.controllersAddToLg}px) {
            margin-top: 16px;
            margin-left: 4px;
          }
          @media (max-width: ${Metrics.breakpoints.sm + 200}px) {
            margin-top: 16px;
            margin-left: 16px;
          }
        `
      : css`
          @media (max-width: ${Metrics.breakpoints.lg}px) {
            margin-top: 16px;
            margin-left: 4px;
          }
          @media (max-width: ${Metrics.breakpoints.sm}px) {
            margin-top: 16px;
            margin-left: 16px;
          }
        `}
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top: -10px;
  @media (max-width: ${Metrics.breakpoints.lg}px) {
    margin-top: 0;
    padding-left: 4px;
  }
  @media (max-width: ${Metrics.breakpoints.md}px) {
    height: 60px;
  }
  @media (max-width: ${Metrics.breakpoints.sm}px) {
    height: 56px;
    padding-left: 16px;
  }
`;

const DistributorAndCustomer = styled.div`
  color: ${Colors.lightGreyOnBlack};
  font-size: 16px;
  @media (max-width: ${Metrics.breakpoints.md}px) {
    font-size: 14px;
  }
`;

const PageTitle = styled.h1`
  margin-left: 20px;
  margin-top: 11px;
  font-size: 2.2em;
  @media (max-width: ${Metrics.breakpoints.lg}px) {
    font-size: 1.7em;
    margin: 12px 16px 18px 16px;
  }
  @media (max-width: ${Metrics.breakpoints.md}px) {
    font-size: 1.5em;
    margin: 12px 10px 18px 10px;
  }
  @media (max-width: ${Metrics.breakpoints.sm}px) {
    font-size: 1.3em;
    margin: 12px 10px 18px 10px;
  }
`;

const UpdateContainer = styled.div`
  margin-left: 20px;
  margin-bottom: 5px;
`;

const Header = (props) => {
  const { selectControllersUpdate, applyControllersUpdate } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const distributorIdSub = useSelector((state) => state.auth.distributorIdSub);
  const role = useSelector((state) => state.auth.role);
  const isSystemAdmin = checkRole(role, 'systemAdmin');
  const distributors = useSelector((state) => state.distributors.distributors);
  const distributorInfo = useSelector((state) => state.distributors.distributorInfo);
  const selectedDistributorInfo = isSystemAdmin
    ? distributors.find((distributor) => distributor.distributorIdSub === distributorIdSub)
    : distributorInfo;
  let distributorName = t('distributor');
  if (selectedDistributorInfo) {
    distributorName =
      selectedDistributorInfo.distributorName ||
      selectedDistributorInfo.address.fullName ||
      selectedDistributorInfo.address.firstName;
  }

  let customerName = t('customer.noCustomer');
  const customerIdSub = useSelector((state) => state.auth.customerIdSub);
  const customers = useSelector((state) => state.customers.customers);
  const selectedCustomerInfo = customers.find((customer) => customer.customerIdSub === customerIdSub);
  if (selectedCustomerInfo) {
    customerName =
      selectedCustomerInfo.customerName ||
      selectedCustomerInfo.address.fullName ||
      selectedCustomerInfo.address.firstName;
  }

  const currentRoute = routesData.find((route) => route.route === props.location.pathname);
  const isControllers = currentRoute?.route === '/controllers';
  const smallerThanSmall = useMediaQuery(`(max-width: ${Metrics.breakpoints.sm}px)`);
  const biggerThanLarge = useMediaQuery(
    `(min-width: ${Metrics.breakpoints.lg + (isControllers ? Metrics.controllersAddToLg : 0)}px)`
  );

  return (
    <>
      <TopContainer className="TopContainer" isControllers={isControllers}>
        <DistributorAndCustomer className="DistributorAndCustomer">
          {distributorName}
          {' > '}
          {customerName}
        </DistributorAndCustomer>
        {biggerThanLarge && <LanguageSelector />}
      </TopContainer>
      <TitleContainer className="TitleContainer">
        {currentRoute?.isPng ? (
          <img
            src={Images[currentRoute.icon]}
            alt={currentRoute.name}
            width={biggerThanLarge ? 40 : smallerThanSmall ? 24 : 32}
          />
        ) : currentRoute?.icon ? (
          <SvgIcon
            icon={currentRoute.icon}
            color={Colors.orange}
            width={biggerThanLarge ? 40 : smallerThanSmall ? 24 : 32}
          />
        ) : null}
        {currentRoute && (
          <>
            <PageTitle className="PageTitle" style={{ color: 'white' }}>
              {t(currentRoute.name)}
            </PageTitle>
            {isControllers && (
              <UpdateContainer className="UpdateContainer">
                <Button
                  type="button"
                  variant="outlined"
                  className={clsx([classes.button, classes.smallSizeButton])}
                  onClick={selectControllersUpdate}
                >
                  Select update
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  className={clsx([classes.button, classes.saveButton, classes.smallSizeButton])}
                  onClick={applyControllersUpdate}
                  disabled={!props.isFileUploaded}
                >
                  Apply update
                </Button>
              </UpdateContainer>
            )}
          </>
        )}
      </TitleContainer>
    </>
  );
};

export default withRouter(Header);
